import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const statuses = ["submitted", "under_review", "documents_pending", "approved", "rejected", "awaiting_payment", "awaiting_lease", "completed"];
const statusColors = {
  submitted: "bg-blue-50 text-blue-700",
  under_review: "bg-amber-50 text-amber-700",
  documents_pending: "bg-orange-50 text-orange-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
  awaiting_payment: "bg-purple-50 text-purple-700",
  awaiting_lease: "bg-indigo-50 text-indigo-700",
  completed: "bg-emerald-50 text-emerald-700",
};

export default function AdminApplicationsPage() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const fetchApps = (status) => {
    setLoading(true);
    const params = status && status !== "all" ? { status } : {};
    api.get("/admin/applications", { params })
      .then((r) => setApps(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchApps(tab); }, [tab]);

  const updateStatus = async (appId, newStatus) => {
    try {
      await api.put(`/admin/applications/${appId}/status`, { status: newStatus });
      toast.success(`Application ${newStatus.replace(/_/g, " ")}`);
      fetchApps(tab);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout title="Applications">
      <div className="space-y-6" data-testid="admin-applications-page">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
            <TabsTrigger value="submitted" className="text-xs">Submitted</TabsTrigger>
            <TabsTrigger value="under_review" className="text-xs">Under Review</TabsTrigger>
            <TabsTrigger value="approved" className="text-xs">Approved</TabsTrigger>
            <TabsTrigger value="rejected" className="text-xs">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>

        <Card className="border-border/60">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : apps.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No applications found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Applicant</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Room</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apps.map((a) => (
                    <TableRow key={a._id} data-testid={`app-row-${a._id}`}>
                      <TableCell className="font-medium">{a.user_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{a.user_email}</TableCell>
                      <TableCell>{a.room_number} ({a.room_category})</TableCell>
                      <TableCell><Badge className={statusColors[a.status]}>{a.status?.replace(/_/g, " ")}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Select onValueChange={(v) => updateStatus(a._id, v)}>
                          <SelectTrigger className="h-8 w-[140px] text-xs" data-testid={`status-select-${a._id}`}>
                            <SelectValue placeholder="Update" />
                          </SelectTrigger>
                          <SelectContent>
                            {statuses.filter((s) => s !== a.status).map((s) => (
                              <SelectItem key={s} value={s} className="text-xs">{s.replace(/_/g, " ")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
